import type { GuidanceState, GuidanceStateUpdate, UserActivityMetrics } from '../types/dashboard';

import { DEFAULT_GUIDANCE_PREFERENCES, DEFAULT_GUIDANCE_INTERACTIONS } from '../types/dashboard';

import { logger } from './logger';
import { SessionManager, STORAGE_KEYS } from './guidanceStateManager.session';

/**
 * Guidance state management utility
 */
export class GuidanceStateManager {
  private static instance: GuidanceStateManager;
  private sessionManager: SessionManager;
  private currentState: GuidanceState | null = null;
  private stateChangeListeners: ((state: GuidanceState) => void)[] = [];

  private constructor() {
    this.sessionManager = new SessionManager();
    this.loadState();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleBeforeUnload = (): void => {
    this.sessionManager.endSession();
    this.persistState();
  };

  destroy(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.stateChangeListeners = [];
  }

  static getInstance(): GuidanceStateManager {
    if (!GuidanceStateManager.instance) {
      GuidanceStateManager.instance = new GuidanceStateManager();
    }
    return GuidanceStateManager.instance;
  }

  /**
   * Load guidance state from localStorage
   */
  private loadState(): void {
    try {
      const savedState = localStorage.getItem(STORAGE_KEYS.GUIDANCE_STATE);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        // Convert date strings back to Date objects
        if (parsed.lastShown) {
          parsed.lastShown = new Date(parsed.lastShown);
        }
        if (parsed.interactions?.lastInteractionDate) {
          parsed.interactions.lastInteractionDate = new Date(
            parsed.interactions.lastInteractionDate,
          );
        }
        this.currentState = parsed;
      }
    } catch (error) {
      logger.warn('[GuidanceStateManager] Failed to load state from localStorage:', { error });
    }
  }

  /**
   * Persist guidance state to localStorage
   */
  private persistState(): void {
    if (!this.currentState) return;

    try {
      localStorage.setItem(STORAGE_KEYS.GUIDANCE_STATE, JSON.stringify(this.currentState));
    } catch (error) {
      logger.warn('[GuidanceStateManager] Failed to persist state to localStorage:', { error });
    }
  }

  /**
   * Get current guidance state
   */
  getState(): GuidanceState | null {
    return this.currentState;
  }

  /**
   * Update guidance state
   */
  updateState(update: GuidanceStateUpdate): void {
    if (!this.currentState) {
      this.currentState = this.createDefaultState();
    }

    const previousState = { ...this.currentState };

    // Apply updates
    if (update.currentStep !== undefined) {
      this.currentState.currentStep = update.currentStep;
      this.trackStepChange(previousState.currentStep, update.currentStep);
    }

    if (update.completedSteps !== undefined) {
      this.currentState.completedSteps = update.completedSteps;
      this.currentState.completionRate = update.completedSteps.length / 5; // Assuming 5 total steps
    }

    if (update.dismissed !== undefined) {
      this.currentState.dismissed = update.dismissed;
      if (update.dismissed) {
        this.trackDismissal();
      }
    }

    if (update.enabled !== undefined) {
      this.currentState.enabled = update.enabled;
    }

    if (update.preferences) {
      this.currentState.userPreferences = {
        ...this.currentState.userPreferences,
        ...update.preferences,
      };
    }

    if (update.interactions) {
      this.currentState.interactions = {
        ...this.currentState.interactions,
        ...update.interactions,
        lastInteractionDate: new Date(),
      };
    }

    this.currentState.lastShown = new Date();

    // Persist changes
    this.persistState();

    // Notify listeners
    this.notifyStateChange();

    // Track the update
    this.sessionManager.trackEvent('guidance_interaction', {
      update,
      previousStep: previousState.currentStep,
      newStep: this.currentState.currentStep,
    });
  }

  /**
   * Create default guidance state
   */
  private createDefaultState(): GuidanceState {
    return {
      enabled: true,
      currentStep: 0,
      completedSteps: [],
      dismissed: false,
      lastShown: new Date(),
      completionRate: 0,
      userPreferences: { ...DEFAULT_GUIDANCE_PREFERENCES },
      interactions: { ...DEFAULT_GUIDANCE_INTERACTIONS },
    };
  }

  /**
   * Track step changes
   */
  private trackStepChange(previousStep: number, newStep: number): void {
    if (!this.currentState) return;

    const now = Date.now();

    // Calculate time spent on previous step
    if (previousStep !== newStep) {
      const timeSpent =
        now - (this.currentState.interactions.lastInteractionDate?.getTime() || now);
      this.currentState.interactions.stepCompletionTimes[`step_${previousStep}`] = timeSpent;
      this.currentState.interactions.totalTimeSpent += timeSpent;
      this.currentState.interactions.totalStepsCompleted++;
    }
  }

  /**
   * Track guidance dismissal
   */
  private trackDismissal(): void {
    if (!this.currentState) return;

    this.currentState.interactions.dismissalCount++;
    this.sessionManager.trackEvent('guidance_interaction', {
      action: 'dismissed',
      step: this.currentState.currentStep,
    });
  }

  /**
   * Track guidance completion
   */
  trackCompletion(): void {
    if (!this.currentState) return;

    this.currentState.interactions.completionCount++;
    this.currentState.completionRate = 1;

    this.sessionManager.trackEvent('guidance_interaction', {
      action: 'completed',
      totalTime: this.currentState.interactions.totalTimeSpent,
      stepsCompleted: this.currentState.interactions.totalStepsCompleted,
    });
  }

  /**
   * Reset guidance state
   */
  resetState(): void {
    this.currentState = this.createDefaultState();
    this.persistState();
    this.notifyStateChange();

    this.sessionManager.trackEvent('guidance_interaction', {
      action: 'reset',
    });
  }

  /**
   * Add state change listener
   */
  addStateChangeListener(listener: (state: GuidanceState) => void): void {
    this.stateChangeListeners.push(listener);
  }

  /**
   * Remove state change listener
   */
  removeStateChangeListener(listener: (state: GuidanceState) => void): void {
    const index = this.stateChangeListeners.indexOf(listener);
    if (index > -1) {
      this.stateChangeListeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyStateChange(): void {
    if (this.currentState) {
      this.stateChangeListeners.forEach((listener) => {
        try {
          listener(this.currentState!);
        } catch (error) {
          logger.error('[GuidanceStateManager] Error in state change listener:', { error });
        }
      });
    }
  }

  /**
   * Get activity metrics
   */
  getActivityMetrics(): UserActivityMetrics {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.ACTIVITY_METRICS);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert date strings back to Date objects
        if (parsed.lastActiveDate) {
          parsed.lastActiveDate = new Date(parsed.lastActiveDate);
        }
        return parsed;
      }
    } catch (error) {
      logger.warn('[GuidanceStateManager] Failed to load activity metrics:', { error });
    }

    return {
      repositoriesConnected: 0,
      analysesCompleted: 0,
      lastActiveDate: new Date(),
      featuresUsed: [],
      sessionsCount: 0,
      totalTimeSpent: 0,
      averageSessionDuration: 0,
      guidanceInteractions: {
        stepsCompleted: 0,
        timeSpent: 0,
        dismissalCount: 0,
        completionRate: 0,
        averageStepTime: 0,
        mostSkippedSteps: [],
        preferredCompletionPath: [],
      },
    };
  }

  /**
   * Update activity metrics
   */
  updateActivityMetrics(update: Partial<UserActivityMetrics>): void {
    const current = this.getActivityMetrics();
    const updated = {
      ...current,
      ...update,
      lastActiveDate: new Date(),
    };

    try {
      localStorage.setItem(STORAGE_KEYS.ACTIVITY_METRICS, JSON.stringify(updated));
    } catch (error) {
      logger.warn('[GuidanceStateManager] Failed to persist activity metrics:', { error });
    }
  }

  /**
   * Track feature usage
   */
  trackFeatureUsage(feature: string): void {
    const metrics = this.getActivityMetrics();
    if (!metrics.featuresUsed.includes(feature)) {
      metrics.featuresUsed.push(feature);
      this.updateActivityMetrics(metrics);
    }

    this.sessionManager.trackEvent('feature_used', { feature });
  }

  /**
   * Get session manager
   */
  getSessionManager(): SessionManager {
    return this.sessionManager;
  }

  /**
   * Clear all stored data (for testing or reset)
   */
  clearAllData(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    this.currentState = null;
    this.sessionManager = new SessionManager();
  }
}

// Export singleton instance
export const guidanceStateManager = GuidanceStateManager.getInstance();
